import React from 'react';
import { getCategoryLabel } from '../documentFilters';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Trash2 } from 'lucide-react';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { DATE_TIME_FORMAT, formatDateSafe, formatFileSize, getBestDate } from '@utils/dateUtils';
import { formatDocumentTitle } from '@utils/documentUtils';
import { MAX_VISIBLE_TAGS } from '@constants/documentDetail';
import type { DocumentDetailModel } from '@hooks/useDocumentDetail';

interface DocumentDetailHeaderProps {
  doc: DocumentDetailModel;
  /** Replaces history, so the highlight URL does not linger in the back stack. */
  onBack: () => void;
  onNavigateToList: () => void;
  onDelete?: () => void;
}

/** Pre-existing rule: first letter of each space-separated word. */
const leadingLetters = (name: string) =>
  name
    .split(' ')
    .map((part) => part[0])
    .join('');

const DocumentDetailHeader: React.FC<DocumentDetailHeaderProps> = ({
  doc,
  onBack,
  onNavigateToList,
  onDelete,
}) => {
  const { t, i18n } = useTranslation();
  const title = formatDocumentTitle(doc.title, doc.metadata?.fileName || doc.fileName);
  const tags: string[] = doc.tags ?? [];

  return (
    <div className="mb-6">
      <Breadcrumb className="mb-4">
        <BreadcrumbList>
          <BreadcrumbItem>
            {/* href and handler both matter: without href middle-click and
                "open in new tab" break, without the handler the crumb is a
                full page reload. */}
            <BreadcrumbLink
              href="/documents"
              onClick={(event) => {
                event.preventDefault();
                onNavigateToList();
              }}
              className="revy-cjk cursor-pointer"
            >
              {t('navigation.documents')}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator>/</BreadcrumbSeparator>
          <BreadcrumbItem>
            <BreadcrumbPage className="revy-cjk">{title}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <Card className="overflow-hidden p-6">
        {/* MUI's lg is 1200px, Tailwind's is 1024 — the arbitrary query keeps
            the breakpoint where it was. */}
        <div className="flex flex-col items-start justify-between gap-4 min-[1200px]:flex-row min-[1200px]:gap-0">
          <div className="min-w-0 flex-1 overflow-hidden">
            <div className="mb-4 flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="mr-2 shrink-0"
                onClick={onBack}
                aria-label={t('documents.returnToDocuments')}
              >
                <ArrowLeft strokeWidth={1.9} />
              </Button>
              <h1 className="revy-cjk min-w-0 break-words hyphens-auto text-2xl font-medium leading-[1.4]">
                {title}
              </h1>
            </div>

            <p className="revy-cjk mb-4 text-base leading-[1.5] text-muted-foreground">
              {doc.summary || doc.description}
            </p>

            <div className="mb-4 flex max-w-[80%] flex-wrap gap-2">
              <Badge variant="outline" className="revy-cjk border-primary text-primary">
                {getCategoryLabel(doc)}
              </Badge>
              {tags.slice(0, MAX_VISIBLE_TAGS).map((tag) => (
                <Badge key={tag} variant="outline" className="max-w-[200px]">
                  {/* Truncation lived in MUI's .MuiChip-label, i.e. on the label
                      element rather than the badge root. */}
                  <span className="revy-cjk overflow-hidden text-ellipsis whitespace-nowrap">
                    {tag}
                  </span>
                </Badge>
              ))}
              {tags.length > MAX_VISIBLE_TAGS && (
                <Badge variant="outline" className="revy-cjk revy-figure">
                  {t('common.moreCount', { count: tags.length - MAX_VISIBLE_TAGS })}
                </Badge>
              )}
            </div>

            <div className="flex items-center gap-4">
              <Avatar className="size-8">
                <AvatarFallback className="revy-cjk text-xs font-medium leading-[1.5]">
                  {leadingLetters(doc.uploadedBy)}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <p className="revy-cjk text-sm leading-[1.5]">
                  {t('documents.uploadedBy')} <strong>{doc.uploadedBy}</strong>
                </p>
                <p className="revy-figure text-xs text-muted-foreground">
                  {formatDateSafe(getBestDate(doc), DATE_TIME_FORMAT, { t, language: i18n.language })} •{' '}
                  {formatFileSize(doc.size || doc.fileSize)} •{' '}
                  {doc.version ? `v${doc.version}` : 'v1'}
                </p>
              </div>
            </div>
          </div>
          {onDelete && (
            <Button
              variant="destructive"
              className="shrink-0"
              onClick={onDelete}
            >
              <Trash2 strokeWidth={1.8} />
              <span className="revy-cjk">{t('common.delete')}</span>
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};

export default DocumentDetailHeader;
